import React, { useState, useEffect } from 'react';
import api from '../api';
import { formatCurrency } from '../utils/formatNumber';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface Client {
  id: number;
  name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  notes?: string;
  logo_url?: string;
  is_active: boolean;
  product_count: number;
}

interface ClientProduct {
  id: number;
  product_id: number;
  sku: string;
  name: string;
  description?: string;
  price?: number;
  brand?: string;
  image_url?: string;
  notes?: string;
}

interface Props {
  products: Array<{ id: number; sku: string; name: string }>;
}

const ClientCatalog: React.FC<Props> = ({ products }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientProducts, setClientProducts] = useState<ClientProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Create client form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientContact, setNewClientContact] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');

  // Tag products
  const [showTagModal, setShowTagModal] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  // Export
  const [exporting, setExporting] = useState(false);

  // Logo upload
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string; type: string}>({show: false, id: null, name: '', type: ''});

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/clients/');
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchClientProducts = async (client: Client) => {
    try {
      const res = await api.get(`/api/clients/${client.id}/products`);
      setClientProducts(res.data.products || []);
    } catch (e) { console.error(e); }
  };

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    fetchClientProducts(client);
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) return;
    try {
      await api.post('/api/clients/', { name: newClientName.trim(), contact_person: newClientContact, email: newClientEmail });
      setShowCreateForm(false);
      setNewClientName(''); setNewClientContact(''); setNewClientEmail('');
      fetchClients();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleDeleteClient = async (id: number) => {
    const client = clients.find(c => c.id === id);
    setDeleteConfirm({show: true, id, name: client?.name || `Client #${id}`, type: 'client'});
  };

  const handleRemoveProduct = async (productId: number) => {
    if (!selectedClient) return;
    const cp = clientProducts.find(p => p.product_id === productId);
    setDeleteConfirm({show: true, id: productId, name: cp?.sku || `Product #${productId}`, type: 'product'});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      if (deleteConfirm.type === 'client') {
        await api.delete(`/api/clients/${deleteConfirm.id}`);
        if (selectedClient?.id === deleteConfirm.id) { setSelectedClient(null); setClientProducts([]); }
        fetchClients();
      } else if (deleteConfirm.type === 'product' && selectedClient) {
        await api.delete(`/api/clients/${selectedClient.id}/products/${deleteConfirm.id}`);
        fetchClientProducts(selectedClient);
        fetchClients();
      }
      setDeleteConfirm({show: false, id: null, name: '', type: ''});
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  const handleTagProducts = async () => {
    if (!selectedClient || selectedProductIds.length === 0) return;
    try {
      await api.post(`/api/clients/${selectedClient.id}/products`, { product_ids: selectedProductIds });
      setShowTagModal(false);
      setSelectedProductIds([]);
      setTagSearch('');
      fetchClientProducts(selectedClient);
      fetchClients();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleLogoUpload = async (file: File) => {
    if (!selectedClient) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/api/clients/${selectedClient.id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Update local state
      const updated = { ...selectedClient, logo_url: res.data.logo_url };
      setSelectedClient(updated);
      setClients(prev => prev.map(c => c.id === updated.id ? { ...c, logo_url: res.data.logo_url } : c));
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!selectedClient) return;
    try {
      await api.delete(`/api/clients/${selectedClient.id}/logo`);
      const updated = { ...selectedClient, logo_url: undefined };
      setSelectedClient(updated);
      setClients(prev => prev.map(c => c.id === updated.id ? { ...c, logo_url: undefined } : c));
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to remove logo');
    }
  };

  const handleExportPdf = async (clientId?: number) => {
    setExporting(true);
    try {
      const url = clientId ? `/api/clients/${clientId}/export/pdf` : '/api/clients/export/pdf/all';
      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', clientId ? `${selectedClient?.name || 'Client'}_Catalog.pdf` : 'Full_Catalog.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const filteredProducts = products.filter(p => {
    if (!tagSearch) return true;
    const s = tagSearch.toLowerCase();
    return p.sku.toLowerCase().includes(s) || p.name.toLowerCase().includes(s);
  });

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedClientProducts = [...clientProducts].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = (a as any)[sortField];
    let bVal: any = (b as any)[sortField];
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (sortField === 'price') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="split-master-detail" style={{ height: '100%' }}>
      {/* Client List Panel */}
      <div className="split-list-plain" style={{ width: '280px', borderRight: '1px solid #e8ecf0', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #e8ecf0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Clients</h3>
          <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 10px' }} onClick={() => setShowCreateForm(true)}>+ New</button>
        </div>

        {/* Export All button */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f3f5' }}>
          <button className="btn btn-secondary" style={{ width: '100%', fontSize: '12px', justifyContent: 'center' }} onClick={() => handleExportPdf()} disabled={exporting}>
            {exporting ? 'Exporting...' : '📄 Export All (PDF)'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div> :
            clients.length === 0 ? <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No clients yet</div> :
            clients.map(client => (
              <button
                key={client.id}
                onClick={() => handleSelectClient(client)}
                style={{
                  width: '100%', padding: '12px 16px', border: 'none', borderBottom: '1px solid #f0f3f5',
                  background: selectedClient?.id === client.id ? '#f0fdfa' : 'transparent',
                  borderLeft: selectedClient?.id === client.id ? '3px solid #0d9488' : '3px solid transparent',
                  textAlign: 'left', cursor: 'pointer', fontSize: '13px'
                }}
              >
                <div style={{ fontWeight: 600, color: '#1e293b' }}>{client.name}</div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                  {client.product_count} products
                </div>
              </button>
            ))
          }
        </div>
      </div>

      {/* Client Products Panel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {!selectedClient ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '14px' }}>
            Select a client to view their catalog
          </div>
        ) : (
          <>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                {/* Logo */}
                <div style={{ position: 'relative' }}>
                  {selectedClient.logo_url ? (
                    <div style={{ position: 'relative', width: '56px', height: '56px' }}>
                      <img
                        src={selectedClient.logo_url}
                        alt={`${selectedClient.name} logo`}
                        style={{ width: '56px', height: '56px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white' }}
                      />
                      <button
                        onClick={handleRemoveLogo}
                        title="Remove logo"
                        style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', border: 'none', background: '#ef4444', color: 'white', fontSize: '11px', lineHeight: '1', cursor: 'pointer', padding: 0 }}
                      >×</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      style={{ width: '56px', height: '56px', borderRadius: '8px', border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#64748b', fontSize: '10px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}
                    >
                      {uploadingLogo ? '...' : <><span style={{ fontSize: '16px' }}>+</span><span>Logo</span></>}
                    </button>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); }}
                  />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{selectedClient.name}</h3>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>{clientProducts.length} products</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {selectedClient.logo_url && (
                  <button className="btn btn-secondary" style={{ fontSize: '12px' }} onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}>
                    {uploadingLogo ? 'Uploading...' : '🖼 Change Logo'}
                  </button>
                )}
                <button className="btn btn-secondary" style={{ fontSize: '12px' }} onClick={() => setShowTagModal(true)}>+ Tag Products</button>
                <button className="btn btn-primary" style={{ fontSize: '12px' }} onClick={() => handleExportPdf(selectedClient.id)} disabled={exporting || clientProducts.length === 0}>
                  {exporting ? 'Exporting...' : '📄 Export PDF'}
                </button>
                <button className="btn btn-danger" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => handleDeleteClient(selectedClient.id)}>Delete</button>
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'auto' }}>
              {clientProducts.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>📦</div>
                  No products tagged yet. Click "Tag Products" to add products to this client's catalog.
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}></th>
                      <th onClick={() => handleSort('sku')} style={{ cursor: 'pointer' }}>SKU {sortField === 'sku' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                      <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                      <th onClick={() => handleSort('brand')} style={{ cursor: 'pointer' }}>Brand {sortField === 'brand' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                      <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>Price {sortField === 'price' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedClientProducts.map(cp => (
                      <tr key={cp.id}>
                        <td style={{ padding: '6px 8px' }}>
                          {cp.image_url ? (
                            <img src={cp.image_url} alt="" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px' }} />
                          ) : (
                            <div style={{ width: '36px', height: '36px', background: '#f1f5f9', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>□</div>
                          )}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{cp.sku}</td>
                        <td>{cp.name}</td>
                        <td style={{ color: '#64748b' }}>{cp.brand || '—'}</td>
                        <td>{cp.price ? formatCurrency(cp.price) : '—'}</td>
                        <td>
                          <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => handleRemoveProduct(cp.product_id)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Client Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '400px' }}>
            <button className="modal-close" onClick={() => setShowCreateForm(false)} aria-label="Close">×</button>
            <h3>New Client</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Client Name *</label>
              <input type="text" value={newClientName} onChange={(e) => setNewClientName(e.target.value)} placeholder="e.g., Salesforce, Google" style={{ width: '100%', padding: '9px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Contact Person</label>
              <input type="text" value={newClientContact} onChange={(e) => setNewClientContact(e.target.value)} placeholder="Name" style={{ width: '100%', padding: '9px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Email</label>
              <input type="email" value={newClientEmail} onChange={(e) => setNewClientEmail(e.target.value)} placeholder="email@company.com" style={{ width: '100%', padding: '9px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '14px' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-cancel" onClick={() => setShowCreateForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateClient} disabled={!newClientName.trim()}>Create Client</button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Products Modal */}
      {showTagModal && selectedClient && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px', maxHeight: '80vh' }}>
            <button className="modal-close" onClick={() => { setShowTagModal(false); setSelectedProductIds([]); setTagSearch(''); }} aria-label="Close">×</button>
            <h3>Tag Products to {selectedClient.name}</h3>
            
            <input
              type="text"
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
              placeholder="Search by SKU or name..."
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}
            />
            
            <div style={{ maxHeight: '350px', overflow: 'auto', border: '1px solid #e8ecf0', borderRadius: '6px' }}>
              {filteredProducts.slice(0, 50).map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', fontSize: '13px' }}>
                  <input
                    type="checkbox"
                    checked={selectedProductIds.includes(p.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedProductIds([...selectedProductIds, p.id]);
                      else setSelectedProductIds(selectedProductIds.filter(id => id !== p.id));
                    }}
                  />
                  <span style={{ fontFamily: 'monospace', color: '#64748b', fontSize: '11px', minWidth: '120px' }}>{p.sku}</span>
                  <span>{p.name}</span>
                </label>
              ))}
            </div>

            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>{selectedProductIds.length} selected</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-cancel" onClick={() => { setShowTagModal(false); setSelectedProductIds([]); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleTagProducts} disabled={selectedProductIds.length === 0}>
                  Tag {selectedProductIds.length} Product{selectedProductIds.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title={deleteConfirm.type === 'client' ? 'Delete Client' : 'Remove Product from Client'}
        message={deleteConfirm.type === 'client'
          ? 'Delete this client and all tagged products? This action cannot be undone.'
          : 'Are you sure you want to remove this product from the client catalog?'}
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: '', type: ''})}
      />
    </div>
  );
};

export default ClientCatalog;
